// components/charts/HsHeatMap.tsx
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { chartColors, colors } from '../../../config/colors';

const HeatMapWrapper = styled.div`
  width: 100%;
  height: 300px;
  overflow-x: auto;
`;

const HeatMapGrid = styled.div`
  display: grid;
  grid-template-columns: 80px repeat(auto-fit, minmax(40px, 1fr));
  gap: 2px;
  min-width: 100%;
`;

const HeatMapCell = styled.div<{ intensity: number }>`
  aspect-ratio: 1;
  background: ${({ intensity, theme }) => {
    if (intensity === 0) return theme.colors.gray200;
    if (intensity < 0.25) return chartColors.heatmap[0];
    if (intensity < 0.5) return chartColors.heatmap[1];
    if (intensity < 0.75) return chartColors.heatmap[2];
    return chartColors.heatmap[3];
  }};
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 500;
  color: ${({ intensity }) => (intensity > 0.5 ? colors.white : colors.neutral[700])};
  cursor: pointer;
  transition: transform 0.2s;

  &:hover {
    transform: scale(1.1);
  }
`;

const HeatMapLabel = styled.div`
  display: flex;
  align-items: center;
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.gray700};
  padding: 0 8px;
`;

const LoadingWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 300px;
  color: ${({ theme }) => theme.colors.gray500};
`;

interface HeatMapData {
    label: string;
    values: Array<{ date: string; value: number }>;
}

interface HsHeatMapProps {
    data: HeatMapData[];
    isLoading?: boolean;
}

export const HsHeatMap: FC<HsHeatMapProps> = ({ data, isLoading }) => {
  const { t } = useTranslation();
  if (isLoading) {
    return <LoadingWrapper>{t('common.loading')}</LoadingWrapper>;
  }

  const maxValue = Math.max(...data.flatMap((row) => row.values.map((v) => v.value)));

  return (
    <HeatMapWrapper>
      <HeatMapGrid>
        {data.map((row) => (
          <>
            <HeatMapLabel key={`label-${row.label}`}>{row.label}</HeatMapLabel>
            {row.values.map((cell, idx) => (
              <HeatMapCell
               /* eslint-disable-next-line react/no-array-index-key */
                key={`${row.label}-${idx}`}
                intensity={maxValue > 0 ? cell.value / maxValue : 0}
                title={`${row.label} - ${cell.date}: ${cell.value}`}
              >
                {cell.value > 0 ? cell.value : ''}
              </HeatMapCell>
            ))}
          </>
        ))}
      </HeatMapGrid>
    </HeatMapWrapper>
  );
};
